import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "./Login";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", org: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="register-success">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-4" aria-hidden="true" />
          <h1 className="font-heading text-2xl font-bold tracking-tight mb-2">Registration received</h1>
          <p className="text-sm text-muted-foreground">
            We've sent a verification email to <span className="font-mono">{form.email}</span>. After verification,
            an administrator will review and approve your account.
          </p>
          <Button className="mt-6 rounded-md" onClick={() => navigate("/login")} data-testid="back-to-login-button">
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="overline mb-2">Partner onboarding</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Request an account</h1>
      <p className="text-sm text-muted-foreground mb-6">TAC self-registration · approved by an administrator.</p>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="register-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Contact name</Label>
          <Input id="name" required value={form.name} onChange={upd("name")} data-testid="register-name-input" className="mt-1.5 rounded-md" />
        </div>
        <div>
          <Label htmlFor="org">Organisation</Label>
          <Input id="org" value={form.org} onChange={upd("org")} data-testid="register-org-input" className="mt-1.5 rounded-md" placeholder="Company Pte Ltd" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={upd("email")} data-testid="register-email-input" className="mt-1.5 rounded-md" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={upd("password")} data-testid="register-password-input" className="mt-1.5 rounded-md" />
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full rounded-md">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
          Submit registration
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted-foreground text-center">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-foreground underline" data-testid="login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
